package com.essntl.features.proposal.ui.addeditproposal.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import com.essntl.core.unsplash.ui.UnsplashImage
import com.essntl.core.utils.utils.formatIntMoney
import com.essntl.features.area.domain.model.AreaModel
import com.essntl.features.proposal.ui.addeditproposal.AddEditProposalEvent
import com.essntl.features.proposal.ui.addeditproposal.AddEditProposalNavigation
import com.essntl.features.proposal.ui.addeditproposal.AddEditProposalState
import com.essntl.features.proposal.ui.rememberCommissionIntSum
import com.essntl.features.proposal.ui.rememberPriceIntSum
import com.essntl.features.task.domain.model.TaskModel
import com.essntl.features.task.ui.TaskColumnDialog
import com.essntl.uikit.components.card.CardInfo
import com.essntl.uikit.components.iconbutton.flat.EsLargeFlatIconButton
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.iconbutton.primary.EsSmallPrimaryIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.resources.LocalRes
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.config.CurrencyConfig
import core.dateformat.ui.formatDateRange
import core.dateformat.utils.formatToFullString
import core.dateformat.utils.parseLocalDateOrNull
import org.jetbrains.compose.resources.painterResource

@Composable
fun BaseInformationSummary(
    state: AddEditProposalState,
    onEvent: (AddEditProposalEvent) -> Unit,
    onNavigation: (AddEditProposalNavigation) -> Unit,
    selectedAreaList: List<AreaModel>,
    onEdit: () -> Unit,
) {
    val res = LocalRes.current
    val filteredProposalServices = state.filteredProposalServices

    val priceIntSum = rememberPriceIntSum(filteredProposalServices, state.getAllProposalServicesPrices.data.orEmpty())
    val commissionIntSum = rememberCommissionIntSum(
        proposal = state.proposal.value,
        proposalServiceList = filteredProposalServices,
    )

    var isAllTaskDialogExpanded by rememberSaveable { mutableStateOf(false) }

    val todosCount = remember(state.getAllProposalTask.data) {
        state
            .getAllProposalTask
            .data
            ?.count { it.status == TaskModel.Status.Todo } ?: 0
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
    ) {
        Row(
            verticalAlignment = Alignment.Top,
            horizontalArrangement = Arrangement.Start,
            modifier = Modifier
                .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
                .background(MaterialTheme.esColorScheme.bgSecondary)
                .padding(
                    vertical = EsConstants.SectionVerticalPadding,
                    horizontal = EsConstants.SectionHorizontalPadding,
                )
                .weight(5f),
        ) {
            state.fileState.getAllFile.data?.firstOrNull()?.let { file ->
                UnsplashImage(
                    url = file.url,
                    enabled = state.isEnabled,
                    selectable = false,
                    deletable = false,
                    imageSize = DpSize(190.dp, 130.dp),
                )
            } ?: Image(
                painter = painterResource(res.addImage),
                contentScale = ContentScale.FillBounds,
                contentDescription = "add image",
                modifier = Modifier
                    .width(190.dp)
                    .height(130.dp)
                    .clip(RoundedCornerShape(20.dp)),
            )

            Spacer(modifier = Modifier.width(2.spacing))

            Column(
                verticalArrangement = Arrangement.Top,
                horizontalAlignment = Alignment.Start,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Row(
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    ) {
                        Text(
                            text = state.proposal.value.title.orEmpty(),
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.h3Regular24,
                            overflow = TextOverflow.Ellipsis,
                            maxLines = 1,
                        )

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(1.spacing),
                            modifier = Modifier.clip(RoundedCornerShape(2.spacing))
                                .background(MaterialTheme.esColorScheme.secondary).padding(1.spacing),
                        ) {
                            EsLargeIcon(
                                res = EsIcons.Location,
                            )

                            Text(
                                text = when (selectedAreaList.size) {
                                    1, 2, 3 -> selectedAreaList.joinToString(", ") { it.name.orEmpty() }
                                    else -> "${
                                        selectedAreaList.take(3).joinToString(",") { it.name.orEmpty() }
                                    }, " + "+${selectedAreaList.size - 3}"
                                },
                                textAlign = TextAlign.Start,
                                style = MaterialTheme.esTypography.p1Medium16,
                            )
                        }

                        Box(
                            modifier = Modifier.clip(RoundedCornerShape(2.spacing))
                                .background(MaterialTheme.esColorScheme.secondary).padding(1.spacing),
                        ) {
                            Text(
                                text = listOf(
                                    state.proposal.value.fromDate,
                                    state.proposal.value.toDate,
                                ).formatDateRange(),
                                textAlign = TextAlign.Start,
                                style = MaterialTheme.esTypography.p1Medium16,
                            )
                        }
                    }

                    EsLargeFlatIconButton(
                        onClick = onEdit,
                        iconRes = EsIcons.Edit,
                        enabled = state.isEnabled,
                    )
                }

                Text(
                    text = state.proposal.value.headline.orEmpty(),
                    color = MaterialTheme.esColorScheme.textSecondary,
                    style = MaterialTheme.esTypography.p2Regular14,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Received",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = parseLocalDateOrNull(state.proposal.value.requestReceivedDate)
                                ?.formatToFullString()
                                ?: "-",
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Agent",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = (state.proposal.value.travelAgent?.contact?.prettyName ?: "-"),
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Clients",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = state.proposal.value.guestCount.toString(),
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Total",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = "${CurrencyConfig.defaultCurrencySymbol}${formatIntMoney(priceIntSum)}",
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Commission",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = "${CurrencyConfig.defaultCurrencySymbol}${formatIntMoney(commissionIntSum)}" +
                                state.proposal.value.commission?.let { " (${it}%)" }.orEmpty(),
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Pricing",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = if (state.proposal.value.hidePrices == true)
                                "Hidden" else
                                "Showing",
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Shows Total Only",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = if (state.proposal.value.showsTotalOnly == true)
                                "Yes" else
                                "No",
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }

                    Column(
                        verticalArrangement = Arrangement.Top,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.weight(1f),
                    ) {
                        Text(
                            text = "Branding",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p2Regular14,
                        )

                        Text(
                            text = if (state.proposal.value.hideBranding == true)
                                "Hidden" else
                                "Showing",
                            color = MaterialTheme.esColorScheme.textPrimary,
                            style = MaterialTheme.esTypography.p1Bold16,
                        )
                    }
                }
            }
        }

//        Spacer(modifier = Modifier.width(2.spacing))
//
//        val todosInformation = if (todosCount == 0)
//            "" else
//            "$todosCount not completed"
//
//        CardInfo(
//            title = "Proposal Tasks",
//            information = todosInformation,
//            informationColor = if (todosCount > 0)
//                MaterialTheme.esColorScheme.textWarning.copy(alpha = 0.7f) else
//                Color.White,
//            action = {
//                EsSmallOutlineIconButton(
//                    onClick = { isAllTaskDialogExpanded = true },
//                    iconRes = EsIcons.Eye,
//                )
//
//                Spacer(modifier = Modifier.width(.5f.spacing))
//
//                EsSmallPrimaryIconButton(
//                    onClick = {
//                        onNavigation(AddEditProposalNavigation.AddProposalTask)
//                    },
//                    iconRes = EsIcons.Add,
//                )
//            },
//        )
    }

    if (isAllTaskDialogExpanded)
        TaskColumnDialog(
            getAllTask = state.getAllProposalTask,
            onEditTask = { t ->
                onNavigation(AddEditProposalNavigation.EditTask(t.id))
            },
            onEditTaskStatus = { t, s ->
                onEvent(AddEditProposalEvent.EditTaskStatus(t.id, s))
            },
            onDeleteTask = { t ->
                onEvent(AddEditProposalEvent.DeleteTask(t))
            },
            onDismissRequest = { isAllTaskDialogExpanded = false },
        )
}